/**
 * In-app notifications (decision 84): kinds, the Settings categories users can switch off, and which kinds also send
 * an email. Email only for money and access: payment failed, plan ending, credits used up; everything else is in-app
 * only. Pure; the API stores rows in core `notifications` (kept `retention.notificationDays`, default 90).
 */

export const NOTIFICATION_CATEGORIES = ['transcription', 'scans', 'credits', 'billing', 'collections', 'exports'] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

/** Labels for the Settings checkboxes. */
export const NOTIFICATION_CATEGORY_LABELS: Record<NotificationCategory, string> = {
  transcription: 'Transcriptions finished, failed or delayed',
  scans: 'Scans finished, or stopped by Instagram',
  credits: 'Credits running low, used up or renewed',
  billing: 'Payments and plan changes',
  collections: 'Collections shared with you, and invites accepted',
  exports: 'Exports ready',
};

export const NOTIFICATION_KINDS = {
  transcription_batch: { category: 'transcription', email: false },
  transcription_failed: { category: 'transcription', email: false },
  transcription_delayed: { category: 'transcription', email: false },
  scan_done: { category: 'scans', email: false },
  scan_pushback: { category: 'scans', email: false },
  credits_low: { category: 'credits', email: false },
  credits_exhausted: { category: 'credits', email: true },
  credits_renewed: { category: 'credits', email: false },
  payment_failed: { category: 'billing', email: true },
  plan_ending: { category: 'billing', email: true },
  plan_changed: { category: 'billing', email: false },
  collection_shared: { category: 'collections', email: false },
  invite_accepted: { category: 'collections', email: false },
  export_ready: { category: 'exports', email: false },
} as const satisfies Record<string, { category: NotificationCategory; email: boolean }>;
export type NotificationKind = keyof typeof NOTIFICATION_KINDS;
export const isNotificationKind = (k: string): k is NotificationKind => Object.hasOwn(NOTIFICATION_KINDS, k);

/** Per-category switches (in-app) and one for the account emails. Missing = on. */
export type NotificationPrefs = Record<NotificationCategory, boolean> & { email: boolean };

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  transcription: true, scans: true, credits: true, billing: true, collections: true, exports: true, email: true,
};

/** Stored JSON (users.notification_prefs) → prefs; anything unknown or malformed falls back to on. */
export function parseNotificationPrefs(raw: string | null | undefined): NotificationPrefs {
  let v: unknown = null;
  try { v = raw ? JSON.parse(raw) : null; } catch { v = null; }
  const o = v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  const out = { ...DEFAULT_NOTIFICATION_PREFS };
  for (const k of [...NOTIFICATION_CATEGORIES, 'email'] as const) if (typeof o[k] === 'boolean') out[k] = o[k] as boolean;
  return out;
}

/** A partial update from Settings (only booleans for known keys), merged over the current prefs. */
export function mergeNotificationPrefs(current: NotificationPrefs, patch: unknown): NotificationPrefs {
  const out = { ...current };
  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) return out;
  for (const k of [...NOTIFICATION_CATEGORIES, 'email'] as const) {
    const v = (patch as Record<string, unknown>)[k];
    if (typeof v === 'boolean') out[k] = v;
  }
  return out;
}

/** Whether a kind is shown in-app / emailed under these prefs. Payment failures are always shown in-app. */
export function notificationAllowed(kind: NotificationKind, prefs: NotificationPrefs): { inApp: boolean; email: boolean } {
  const def = NOTIFICATION_KINDS[kind];
  const inApp = kind === 'payment_failed' || prefs[def.category];
  return { inApp, email: def.email && prefs.email };
}

/** Short bounds for stored text (the bell shows a line or two). */
export const NOTIFICATION_TITLE_MAX = 140;
export const NOTIFICATION_BODY_MAX = 400;
/** A link is an app path (`/c/handle`), never an external URL. */
export const isNotificationLink = (link: string | null | undefined): link is string =>
  typeof link === 'string' && /^\/(?!\/)[A-Za-z0-9/_.\-?=&#%@]{0,300}$/.test(link);
